import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, Plus, Trash2, X, GripVertical, Eye } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../contexts/AuthContext';
import AnimalDetail from '../components/AnimalDetail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const placeholderImg = '/sem-foto.jpg';

export default function BoxesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isTecnico = user?.role === 'tecnico';
  const [boxes, setBoxes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [animais, setAnimais] = useState([]);
  const [novaBox, setNovaBox] = useState('');
  const [allBoxNames, setAllBoxNames] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [detailAnimal, setDetailAnimal] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const recarregarAnimais = () => {
    if (selected) axios.get(`${API}/boxes/${encodeURIComponent(selected)}/animais`).then(r => setAnimais(r.data)).catch(console.error);
  };

  useEffect(() => {
    axios.get(`${API}/boxes`).then(r => {
      const sorted = r.data.sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
      setBoxes(sorted);
      setAllBoxNames(sorted.map(b => b.nome));
    }).catch(console.error);
  }, []);

  const selectBox = (nome) => {
    setSelected(nome);
    axios.get(`${API}/boxes/${encodeURIComponent(nome)}/animais`).then(r => setAnimais(r.data)).catch(console.error);
    setShowPopup(true);
  };

  const criar = async () => {
    if (!novaBox.trim()) return;
    const r = await axios.post(`${API}/boxes`, { nome: novaBox.trim() });
    const updated = [...boxes, r.data].sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
    setBoxes(updated);
    setAllBoxNames(updated.map(b => b.nome));
    setNovaBox('');
  };

  const apagar = async (nome) => {
    if (!window.confirm(`Apagar box "${nome}"?`)) return;
    try {
      await axios.delete(`${API}/boxes/${encodeURIComponent(nome)}`);
      setBoxes(boxes.filter(b => b.nome !== nome));
      setAllBoxNames(allBoxNames.filter(n => n !== nome));
      if (selected === nome) { setSelected(null); setShowPopup(false); }
    } catch (e) { alert(e.response?.data?.detail || 'Erro ao apagar'); }
  };

  const moverAnimal = async (animalId, novaBoxName) => {
    await axios.put(`${API}/animais/${animalId}/box`, { box: novaBoxName });
    setAnimais(animais.filter(a => a.id !== animalId));
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = boxes.findIndex(b => b.nome === active.id);
    const newIndex = boxes.findIndex(b => b.nome === over.id);
    const reordered = arrayMove(boxes, oldIndex, newIndex).map((b, i) => ({ ...b, ordem: i + 1 }));
    setBoxes(reordered);
    try {
      await axios.put(`${API}/boxes/reordenar/all`, { boxes: reordered.map(b => ({ nome: b.nome, ordem: b.ordem })) });
    } catch (e) { console.error('Erro ao reordenar:', e); }
  };

  return (
    <div data-testid="boxes-page">
      <h2 className="font-heading font-bold text-2xl mb-6">Gestão de Boxes</h2>
      {isTecnico && (
        <div className="flex gap-3 mb-6">
          <input data-testid="new-box-input" value={novaBox} onChange={e => setNovaBox(e.target.value)} placeholder="Nome da nova box"
            className="rounded-xl border border-border bg-card px-3 py-2 text-sm w-48 focus:ring-2 focus:ring-primary focus:outline-none" onKeyDown={e => e.key === 'Enter' && criar()} />
          <button data-testid="create-box-btn" onClick={criar} className="flex items-center gap-1 bg-primary text-primary-foreground rounded-full px-4 py-2 text-sm font-medium hover:brightness-110 transition-all">
            <Plus className="w-4 h-4" />Criar Box
          </button>
        </div>
      )}

      <p className="text-xs text-muted-foreground mb-3">Arraste para reordenar as boxes.</p>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={boxes.map(b => b.nome)} strategy={verticalListSortingStrategy}>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {boxes.map(b => (
              <SortableBox key={b.nome} box={b} isSelected={selected === b.nome} isTecnico={isTecnico}
                onSelect={() => selectBox(b.nome)} onDelete={() => apagar(b.nome)} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {showPopup && selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="box-popup">
          <div className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col m-4">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-heading font-bold text-xl">Animais na Box: {selected}</h3>
              <button onClick={() => setShowPopup(false)} className="p-1 hover:bg-muted rounded-full"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {animais.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">Nenhum animal nesta box.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {animais.map(a => {
                    const fotoIdx = a.foto_capa_index || 0;
                    const foto = a.fotos?.length > 0 ? a.fotos[Math.min(fotoIdx, a.fotos.length - 1)] : null;
                    const fotoSrc = foto ? ((foto.startsWith('http') || foto.startsWith('data:')) ? foto : `${process.env.REACT_APP_BACKEND_URL}${foto}`) : placeholderImg;
                    return (
                    <div key={a.id} className="bg-muted/30 rounded-xl p-3 flex flex-col gap-2">
                      <div className="flex gap-3 items-start">
                        <img src={fotoSrc}
                          alt={a.nome} className="w-14 h-14 rounded-lg object-cover cursor-pointer flex-shrink-0" onError={e => { e.target.src = placeholderImg; }}
                          onClick={() => setDetailAnimal(a)} />
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setDetailAnimal(a)} data-testid={`box-animal-${a.id}`}>
                          <h4 className="font-heading font-bold text-sm break-words hover:text-primary transition-colors leading-tight">{a.nome}</h4>
                          <p className="text-xs text-muted-foreground capitalize mt-0.5">{a.especie}</p>
                          {a.chip && <p className="text-[11px] font-bold text-primary tracking-wide mt-0.5 break-all">Chip: {a.chip}</p>}
                          <p className={`text-[10px] mt-0.5 ${a.esterilizado ? 'text-green-700' : 'text-amber-700'}`}>
                            {a.esterilizado ? '✓ Esterilizado' : '○ Não esterilizado'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <button onClick={() => setDetailAnimal(a)} title="Ver ficha" data-testid={`box-animal-view-${a.id}`}
                          className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20">
                          <Eye className="w-3.5 h-3.5" /> Ver ficha
                        </button>
                        <select value={a.box || ''} onChange={e => moverAnimal(a.id, e.target.value)}
                          className="text-xs rounded-lg border border-border bg-background px-2 py-1 flex-shrink-0">
                          <option value="">Sem box</option>
                          {allBoxNames.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                    </div>
                  );})}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {detailAnimal && (
        <AnimalDetail animal={detailAnimal}
          onClose={() => setDetailAnimal(null)}
          onUpdate={recarregarAnimais}
          onEdit={(id) => navigate(`/editar/${id}`)} />
      )}
    </div>
  );
}

function SortableBox({ box, isSelected, isTecnico, onSelect, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: box.nome });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} data-testid={`box-${box.nome}`}
      className={`bg-card rounded-2xl border p-4 transition-all duration-200 ${isSelected ? 'border-primary shadow-lg' : 'border-border hover:shadow-md'}`}>
      <div className="flex items-center justify-between mb-2">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded">
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </div>
        <Box className="w-5 h-5 text-primary" />
        {isTecnico && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="text-destructive hover:bg-destructive/10 p-1 rounded-full">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <button onClick={onSelect} className="text-left w-full">
        <h3 className="font-heading font-bold text-foreground">{box.nome}</h3>
        <p className="text-xs text-muted-foreground" data-testid={`box-count-${box.nome}`}>
          {box.num_animais || 0} {(box.num_animais || 0) === 1 ? 'animal' : 'animais'}
        </p>
      </button>
    </div>
  );
}
